import { styled, css } from 'styled-components';
import { Button } from '@sendible/design-system';

export const TriggerButtonWrap = styled.div`
  position: relative;
`;

export const TriggerButton = styled(Button)`
  border-radius: 0.25rem;
`;

export const DropdownPanel = styled.div`
  ${({ theme: { colors, spacings } }) => css`
    background: ${colors.brandLight};
    border-radius: 0.25rem;
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    width: 19rem;
    overflow: hidden;
    z-index: 100;
    padding: ${spacings.s8};

    input {
      margin: 0;
    }

    > ul {
      list-style: none;
      margin: 0;
      max-height: 22rem;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 0.5rem 0;

      > li + li {
        border-top: 1px solid ${colors.brandDarkTint95};
        margin-top: 0.25rem;
        padding-top: 0.25rem;
      }

      ul {
        list-style: none;
        margin: 0;
        max-height: none;
        overflow-y: visible;
        padding: 0;
      }

      &::-webkit-scrollbar {
        height: 8px;
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: white;
      }

      &::-webkit-scrollbar-thumb {
        background-clip: padding-box;
        background-color: ${colors.brandDarkTint80};
        border-radius: 8px;
        -webkit-border-radius: 8px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.brandDarkTint70};
      }
    }
  `}
`;

export const GroupHeaderRow = styled.div`
  ${({ theme: { colors, fontSizes, fontWeights } }) => css`
    cursor: pointer;
    padding: 0.625rem 0.5rem;
    user-select: none;

    &:hover {
      background: ${colors.brandDarkAlpha5};
    }

    span {
      font-size: ${fontSizes.s14};
      font-weight: ${fontWeights.semiBold};
      color: ${colors.brandDark};
    }

    .ds-checkbox {
      gap: 0;

      span {
        height: 1.125em;
        width: 1.125em;
        flex-shrink: 0;
        flex-grow: 0;
        font-size: inherit;
        text-transform: none;
        letter-spacing: normal;
        color: inherit;
      }

      [data-testid='checkbox-label'] {
        display: none !important;
      }

      input {
        width: 100%;
        height: 100%;
      }
    }

    .icon {
      height: 0.875rem;
      width: 0.875rem;
    }
  `}
`;

export const GroupHeaderInner = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

export const ProfileRow = styled.button<{ $isSelected?: boolean }>`
  ${({ theme: { colors }, $isSelected }) => css`
    cursor: pointer;
    padding: 0.625rem 1rem;
    user-select: none;
    border-radius: 0.25rem;
    background: ${$isSelected ? colors.brandDarkAlpha5 : 'transparent'};
    border: none;
    width: 100%;
    text-align: left;

    &:hover {
      background: ${colors.brandDarkAlpha5};
    }

    .ds-checkbox {
      gap: 0;

      span {
        height: 1.125em;
        width: 1.125em;
        flex-shrink: 0;
        flex-grow: 0;
      }

      [data-testid='checkbox-label'] {
        display: none !important;
      }

      input {
        width: 100%;
        height: 100%;
      }
    }
  `}
`;

export const ProfileRowInner = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin-left: 0.25rem;
`;

export const AvatarWrap = styled.div`
  ${({ theme: { colors } }) => css`
    flex-shrink: 0;
    position: relative;
    width: 1.5rem;
    height: 1.5rem;

    img,
    p {
      align-items: center;
      background: ${colors.brandPrimary};
      border-radius: 0.375rem;
      display: flex;
      height: 1.5rem;
      width: 1.5rem;
      justify-content: center;
      margin: 0;
      object-fit: cover;
      object-position: center;
      overflow: hidden;
    }

    p {
      color: ${colors.brandLight};
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}
`;

export const NetworkBadge = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: ${colors.brandLight};
    border-radius: 50%;
    bottom: -0.1875rem;
    box-shadow: 0 0 0 1.5px ${colors.brandLight};
    display: flex;
    height: 0.9375rem;
    justify-content: center;
    position: absolute;
    right: -0.1875rem;
    width: 0.9375rem;
    z-index: 1;

    .icon {
      display: block;
      height: 0.8125rem;
      width: 0.8125rem;
    }
  `}
`;

export const ProfileLabel = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    color: ${colors.brandDark};
    flex: 1;
    font-size: ${fontSizes.s14};
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

export const StatusBadge = styled.span`
  ${({ theme: { colors, fontSizes } }) => css`
    align-items: center;
    background: rgba(249, 115, 22, 0.08);
    border: 1px solid ${colors.semanticWarning};
    border-radius: 0.25rem;
    color: ${colors.semanticWarning};
    display: flex;
    flex-shrink: 0;
    font-size: ${fontSizes.s12};
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    white-space: nowrap;

    .icon {
      height: 0.75rem;
      width: 0.75rem;
    }
  `}
`;

export const EmptyText = styled.p`
  ${({ theme: { colors, fontSizes } }) => css`
    color: ${colors.brandDarkTint30};
    font-size: ${fontSizes.s14};
    margin: 0;
    padding: 0.75rem 1rem;
    text-align: center;
  `}
`;

export const Footer = styled.div`
  ${({ theme: { spacings } }) => css`
    display: flex;
    justify-content: flex-end;
    padding: ${spacings.s0};
  `}
`;
